import React, { FC, Fragment, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import HttpStatus from 'app/components/HttpStatus';
import { SsrModule } from 'app/constants/ssrModules';
import { withinBettingContext } from 'app/contexts/betting';
import { SportId } from 'app/lib/betting/constants';
import { setMatchesContainerHeight } from 'app/modules/Prematch/actions';
import EmptyGame from 'app/widgets/EmptyGame';

import GridHeader from '../GridHeader';
import MatchesContainer from '../MatchesContainer';
import MatchTable from '../MatchTable';
import GridStageName from './StageName';

// ---

type DispatchProp = {
  dispatch: Dispatch;
};

type StoreProps = {
  sportId: SportId;
  // TODO: дописать тип
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  matchIdsByStageId: Map<number, Set<number>>;
};

const BettingStagesGrid: FC<StoreProps & DispatchProp> = ({
  sportId,
  matchIdsByStageId,
  dispatch,
}) => {
  const divRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      dispatch(setMatchesContainerHeight(divRef.current?.clientHeight || null));
    };
  }, [divRef.current]);

  if (matchIdsByStageId.size === 0) {
    return (
      <HttpStatus code={404}>
        <EmptyGame typeText="noFilteredEvents" />
      </HttpStatus>
    );
  }

  const tournamentStageIds = [...matchIdsByStageId.keys()];

  return (
    <div data-ssr-id={SsrModule.STAGE_TABLE}>
      <GridHeader shouldBeLink
                  sportId={sportId} />

      <MatchesContainer ref={divRef}>
        {tournamentStageIds.map((tournamentStageId: number) => (
          <Fragment key={tournamentStageId}>
            <GridStageName sportId={sportId}
                           tournamentStageId={tournamentStageId} />
            <MatchTable sportId={sportId}
                        tournamentStageId={tournamentStageId}
                        matchIds={matchIdsByStageId.get(tournamentStageId)} />
          </Fragment>
        ))}
      </MatchesContainer>
    </div>
  );
};

const createMapStateToProps = (state: ReduxState, {
  $betting: {
    navigation: {
      selectors: {
        currentLocation,
      },
    },
    grid: {
      selectors: {
        stageGridSelector,
      },
    },
  },
}: BettingContextProps): StoreProps => {
  const { sportId, tournamentId } = currentLocation(state);
  const matchIdsByStageId = stageGridSelector(state, tournamentId);

  return {
    sportId,
    matchIdsByStageId,
  };
};

export default compose<FC>(
  withinBettingContext,
  connect(createMapStateToProps),
)(BettingStagesGrid);
